import { processErrors } from "../../../functions/errorHandling";
import IAPI from "../../../models/IAPI";
import { IAPIUpdates } from "../../../models/IAPIUpdates";
import ICompanyBase from "../../../models/ICompanyBase";
import IIntegration from "../../../models/IIntegration";
import { IMenuItem } from "../../../models/IMenuItem";
import ISupportCall, { ISubmittedTicket } from "../../../models/ISupportCall";
import Services from "../../../services/Services";
import snackbarService from "../../../services/ui-messaging/SnackbarService";
import { IPendingItem } from "../../../store/status/types";
import { FormErrors } from "../../forms/FormErrors";
import validateForm from "../../forms/validateForm";

const pendingForm: IPendingItem = {
  id: "8a75a42f-73fb-450e-b73e-26753e4d5f28",
  description: "Adding new integration",
};

export const CustomAPIID = 22;

export const newFormErrors = (): FormErrors<IIntegration> => {
  return {
    ID: {},
    Name: {},
    Company: {},
    Status: {},
    DataDirection: {},
    System: {},
    Module: {},
    Description: {},
    PublishNotes: {},
    DevelopmentURL: {},
  };
};

const requiredFields = ["Company", "Name", "Description", "System", "DataDirection"];

export const validate = (form: IIntegration, setFormErrors: (val: FormErrors<IIntegration>) => void) => {
  validateForm(form, {
    requiredFields,
    formErrors: newFormErrors(),
    onFormError: setFormErrors,
  });
};

export interface IHandleSubmitProps {
  form: IIntegration;
  token: string;
  company: ICompanyBase | undefined;
  addPending: Function;
  completePending: Function;
  setFormErrors: (val: FormErrors<IIntegration>) => void;
  setIntegrationErrors: (val: string[]) => void;
  setAllowEdit: (val: boolean) => void;
}

export interface IHandleAPISubmitProps {
  token: string;
  integrationID: number;
  selectedApis: IAPI[];
  currentApis: IAPI[];
  company: ICompanyBase | undefined;
  setIntegrationErrors: (val: string[]) => void;
}

export const getIntegrationFormFromModel = (model: IIntegration): IIntegration => {
  if (!model) {
    throw new Error("An integration model was not provided");
  }
  return {
    ID: model.ID,
    Name: model.Name,
    Company: {
      ID: model.Company.ID,
      Name: model.Company.Name,
    },
    Status: model.Status,
    DataDirection: model.DataDirection,
    System: model.System,
    Module: model.Module,
    Description: model.Description,
    PublishNotes: model.PublishNotes,
    DevelopmentURL: model.DevelopmentURL,
  };
};

export const handleAPISubmit = async (props: IHandleAPISubmitProps) => {
  const { token, integrationID, selectedApis, currentApis, company, setIntegrationErrors } = props;

  try {
    if (!token) {
      throw new Error("Unauthorized");
    }

    setIntegrationErrors([""]);
    if (selectedApis.length === 0 && currentApis.length === 0) {
      throw new Error("No changes were found to update");
    }

    if (company) {
      const svcs = new Services();
      const integrationRepo = await svcs.IntegrationRepository();

      let updateApis: IAPIUpdates = {
        AddedIntegrationMethodIDs: [],
        RemovedIntegrationMethodIDs: [],
      };

      for (let i = 0; i < selectedApis.length; i++) {
        if (!currentApis.includes(selectedApis[i])) {
          updateApis.AddedIntegrationMethodIDs.push(selectedApis[i].ID);
        }
      }

      for (let i = 0; i < currentApis.length; i++) {
        if (!selectedApis.includes(currentApis[i])) {
          updateApis.RemovedIntegrationMethodIDs.push(currentApis[i].ID);
        }
      }

      var resp = integrationRepo.updateAPIs(token, company.ID, integrationID, updateApis);

      if (resp) {
        return (await resp).filter(a => a.ShowToVendors);
      }
    }
  } catch (err) {
    var processedErrors = processErrors(err);
    setIntegrationErrors(processedErrors);
    snackbarService.error(processedErrors);
  }
};

export const handleSubmit = async (props: IHandleSubmitProps): Promise<IIntegration | undefined> => {
  const { form, token, company, addPending, completePending, setFormErrors, setIntegrationErrors, setAllowEdit } = props;

  try {
    if (!token) {
      throw new Error("Unauthorized");
    }

    validate(form, setFormErrors);

    if (form.ID > 0) {
      let updatePendingForm = {
        ...pendingForm,
        description: "Updating integration...",
      };
      addPending(updatePendingForm);
    } else {
      addPending(pendingForm);
    }

    const svcs = new Services();
    const integrationRepo = await svcs.IntegrationRepository();

    if (company && form.ID <= 0) {
      var integrationResponse = await integrationRepo.add(token, form);

      if (integrationResponse) {
        snackbarService.success(`Integration '${form.Name}' added`);
        setAllowEdit(false);
        try {
          integrationRepo.createDefaultVisibility(token, integrationResponse.ID);
        } catch (e) {
          /* Ignore failure, will be logged by RMS API */
        }
        return integrationResponse;
      } else {
        throw new Error(`Error: Unable to add integration. ${integrationResponse}`);
      }
    } else if (company && form.ID > 0) {
      var integrationResponse = await integrationRepo.update(token, form);

      if (integrationResponse) {
        setFormErrors(newFormErrors());
        snackbarService.success(`Integration '${integrationResponse.Name}' saved`);
        setAllowEdit(false);
        return integrationResponse;
      } else {
        throw new Error(`Error: Unable to update integration. ${integrationResponse}`);
      }
    }
  } catch (err) {
    var processedErrors = processErrors(err);
    setIntegrationErrors(processedErrors);
    snackbarService.error(processedErrors);
  } finally {
    completePending(pendingForm);
  }
};

export interface ISaveStatusProps {
  token: string;
  form: IIntegration;
  status: IMenuItem;
  setForm: (val: IIntegration) => void;
  setFormErrors: (val: FormErrors<IIntegration>) => void;
}

export const saveStatus = async (props: ISaveStatusProps) => {
  const { token, form, status, setForm, setFormErrors } = props;
  const originalForm = form;
  let tempForm = {
    ...form,
    Status: status,
  };

  setForm(tempForm);
  const svcs = new Services();
  const integrationRepo = await svcs.IntegrationRepository();

  try {
    var integrationResponse = await integrationRepo.update(token, tempForm);
    if (integrationResponse) {
      setForm({ ...integrationResponse });
      setFormErrors(newFormErrors());
    } else {
      throw new Error(`Error: Unable to update integration. ${integrationResponse}`);
    }
  } catch (ex) {
    setForm(originalForm);
    throw ex;
  }
};

export async function submitForProductionServiceCall(integration: IIntegration, token: string, username: string, title: string) {
  var call: ISupportCall = {
    id: "0",
    status: "New",
    summary: title,
    description: `## Description
Approval request for Integration to be Available for Customers.

Integration: ${integration.Name}
Company: ${integration.Company.Name}
`,
    dateSubmitted: new Date().toUTCString(),
    submittedBy: username || "partner portal unknown user",
    type: "External",
    menuPath: "IH\\PR\\PS\\PS\\IM",
    product: "",
    productType: "",
    labelPath: "",
    module: "",
    object: "",
    screen: "",
    integrationID: integration.ID.toString(),
  };

  const scRepo = await new Services().ServiceCallRepository();
  return await scRepo.SubmitServiceCall(token, call);
}

export async function submitAPIsUpdateServiceCall(
  token: string,
  username: string,
  integration: IIntegration,
  newAPIs: IAPI[],
  currentAPIs: IAPI[],
  title: string,
  setIntegrationErrors: (val: string[]) => void,
  setAPISCNumber: (val: string) => void,
) {
  try {
    var call: ISupportCall = {
      id: "0",
      status: "New",
      summary: title,
      description: `## Description
Approval request for Integration APIs to be Updated.

Integration: ${integration.Name}
Company: ${integration.Company.Name}

Old APIs: ${currentAPIs.map(oldApi => `${oldApi.Name} `)};

New APIs: ${newAPIs.map(api => `${api.Name} `)}; 
`,
      dateSubmitted: new Date().toUTCString(),
      submittedBy: username || "partner portal unknown user",
      type: "External",
      menuPath: "IH\\PR\\PS\\PS\\IM",
      product: "",
      productType: "",
      labelPath: "",
      module: "",
      object: "",
      screen: "",
      integrationID: integration.ID.toString(),
    };

    const scRepo = await new Services().ServiceCallRepository();
    let resp: ISubmittedTicket = await scRepo.SubmitServiceCall(token, call);
    if (resp.Number && resp.Number !== "") {
      snackbarService.success("API Update Request Submitted Successfully");
      setAPISCNumber(resp.Number);
    }
  } catch (err) {
    let msg: string = (err as Error).message;
    snackbarService.error(msg);
    setIntegrationErrors([msg]);
  }
}
